import { useState, useEffect, useCallback } from "react";
import { TransactionHistoryService } from "../../services/api/transactionHistory.service";
import SummaryCards from "./SummaryCards";
import ClinicListTable from "./ClinicListTable";
import ClinicTransactionView from "./ClinicTransactionView";
import {
  DateRange,
  PaymentSummaryData,
  ClinicFinancialItem,
} from "../../types/transactionHistory.types";

interface AdminTransactionViewProps {
  dateRange: DateRange;
}

export default function AdminTransactionView({ dateRange }: AdminTransactionViewProps) {
  const [summaryData, setSummaryData] = useState<PaymentSummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [selectedClinic, setSelectedClinic] = useState<ClinicFinancialItem | null>(null);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await TransactionHistoryService.getPaymentSummary({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        // No clinicId → aggregated admin view
      });
      if (res.success) {
        setSummaryData(res.data);
      }
    } catch (err) {
      console.error("Error fetching admin summary:", err);
    } finally {
      setSummaryLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Drill-down into a specific clinic
  if (selectedClinic) {
    return (
      <ClinicTransactionView
        dateRange={dateRange}
        clinicId={selectedClinic.clinicId}
        clinicName={selectedClinic.clinicName}
        isAdmin={true}
        onBack={() => setSelectedClinic(null)}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Overall Summary */}
      <SummaryCards
        data={summaryData?.overviewCards || null}
        loading={summaryLoading}
        heading="Overall Summary"
        subtitle={
          summaryData?.timeframe?.clinicIds?.length
            ? `${summaryData.timeframe.clinicIds.length} clinics managed`
            : undefined
        }
      />

      {/* Clinic list */}
      <ClinicListTable
        dateRange={dateRange}
        onClinicClick={(clinic) => setSelectedClinic(clinic)}
      />
    </div>
  );
}
